import { useCallback } from 'react';
import { useCreatePerson } from '#root/queries/persons';
import { VictimFormData } from '#root/pages/VTracker/AllVictims/VictimForm';
import { PERSON_TYPES, SCARS_LABEL, TATTOO_LABEL } from '#root/utils/persons';
import { FILE_KINDS, FILE_REFERENCE_TYPES, useFileUpload, FileUploadPayload } from '#root/hooks/useFileUpload';

export const useVictimOperations = () => {
    const createVictim = useCreatePerson();
    const { uploadImages } = useFileUpload();

    const createVictimOperation = useCallback(async (victim: VictimFormData) => {
        const personResponse = await createVictim.mutateAsync({
            person: {
                ...victim,
                type: PERSON_TYPES.VICTIM,
            }
        });

        if (personResponse?.item?.id) {
            const filesToUpload: FileUploadPayload[] = [];

            if (victim.personalImage) {
                filesToUpload.push({
                    file: victim.personalImage,
                    referenceId: personResponse.item.id,
                    kind: FILE_KINDS.PERSONAL_IMAGE,
                    referenceType: FILE_REFERENCE_TYPES.PERSON
                });
            }

            if (victim.licenseImage) {
                filesToUpload.push({
                    file: victim.licenseImage,
                    referenceId: personResponse.item.id,
                    kind: FILE_KINDS.LICENSE_ID,
                    referenceType: FILE_REFERENCE_TYPES.PERSON
                });
            }

            if (victim.features) {
                if (victim.features.includes(SCARS_LABEL.value) && victim.scarImages.length > 0) {
                    victim.scarImages.forEach(file => {
                        filesToUpload.push({
                            file,
                            referenceId: personResponse.item.id,
                            kind: FILE_KINDS.SCAR,
                            referenceType: FILE_REFERENCE_TYPES.PERSON
                        });
                    });
                }

                if (victim.features.includes(TATTOO_LABEL.value) && victim.tattooImages.length > 0) {
                    victim.tattooImages.forEach(file => {
                        filesToUpload.push({
                            file,
                            referenceId: personResponse.item.id,
                            kind: FILE_KINDS.TATTOO,
                            referenceType: FILE_REFERENCE_TYPES.PERSON
                        });
                    });
                }
            }

            if (filesToUpload.length > 0) {
                await uploadImages(filesToUpload);
            }
        }

        return personResponse;
    }, [createVictim, uploadImages]);

    return {
        createVictim: createVictimOperation
    };
};

export type VictimOperations = ReturnType<typeof useVictimOperations>;
